"use client";

import { type TokenAccountInfo, formatCompactBalance, shortenAddress } from "@/lib/burn";
import { ExternalLink, RefreshCw } from "lucide-react";

const SOLSCAN_URL = "https://solscan.io";

interface BurnTokenListProps {
  tokens: TokenAccountInfo[];
  selectedToken: TokenAccountInfo | null;
  onSelectToken: (token: TokenAccountInfo) => void;
  onRefresh: () => void;
  isRefreshing: boolean;
}

export function BurnTokenList({
  tokens,
  selectedToken,
  onSelectToken,
  onRefresh,
  isRefreshing,
}: BurnTokenListProps) {
  const getTokenName = (token: TokenAccountInfo) => {
    if (token.symbol) return token.symbol;
    if (token.name) return token.name;
    return shortenAddress(token.mint.toBase58(), 4);
  };

  const getTokenSubtitle = (token: TokenAccountInfo) => {
    if (token.name && token.symbol) return token.name;
    return shortenAddress(token.mint.toBase58(), 6);
  };

  return (
    <div className="mb-8">
      {/* Section header */}
      <div className="flex justify-between items-center mb-3">
        <h2 className="text-base font-semibold text-white">Your Tokens</h2>
        <div className="flex items-center gap-3">
          <span className="text-xs text-[#666]">{tokens.length} tokens</span>
          <button
            onClick={onRefresh}
            disabled={isRefreshing}
            className="text-[#f9cd4f] hover:opacity-80 transition-opacity disabled:opacity-50"
            title="Refresh token list"
          >
            <RefreshCw className={`w-4 h-4 ${isRefreshing ? "animate-spin" : ""}`} />
          </button>
        </div>
      </div>

      {/* Token list */}
      <div className="flex flex-col gap-2 max-h-[400px] overflow-y-auto">
        {tokens.map((token) => {
          const isSelected = selectedToken?.pubkey.equals(token.pubkey);

          return (
            <div
              key={token.pubkey.toBase58()}
              onClick={() => onSelectToken(token)}
              className={`flex items-center gap-3 p-3 bg-[#0a0a0a] rounded-xl border-2 cursor-pointer transition-all hover:border-[#f9cd4f]/50 ${
                isSelected ? "border-[#f9cd4f] bg-[#111]" : "border-[#222]"
              }`}
            >
              {/* Token logo */}
              <div className="w-10 h-10 shrink-0">
                {token.logoURI ? (
                  <img
                    src={token.logoURI}
                    alt={token.symbol || "token"}
                    className="w-10 h-10 rounded-full"
                    onError={(e) => {
                      (e.target as HTMLImageElement).style.display = "none";
                      const placeholder = (e.target as HTMLImageElement).nextElementSibling;
                      if (placeholder) {
                        (placeholder as HTMLElement).style.display = "flex";
                      }
                    }}
                  />
                ) : null}
                <div
                  className={`w-10 h-10 rounded-full bg-[#222] items-center justify-center text-[#666] text-base font-semibold ${
                    token.logoURI ? "hidden" : "flex"
                  }`}
                >
                  {(token.symbol?.[0] || "?").toUpperCase()}
                </div>
              </div>

              {/* Token info - truncate long names */}
              <div className="flex-1 min-w-0 overflow-hidden">
                <div className="font-semibold text-[0.95rem] text-white truncate">
                  {getTokenName(token)}
                </div>
                <div className="text-xs text-[#666] truncate">{getTokenSubtitle(token)}</div>
              </div>

              {/* Balance and Solscan link */}
              <div className="flex items-center gap-2 shrink-0">
                <div className="text-right whitespace-nowrap">
                  <span className="font-semibold text-[#f9cd4f]">
                    {formatCompactBalance(token.amount, token.decimals)}
                  </span>
                  {token.symbol && (
                    <span className="text-xs text-[#888] ml-1">{token.symbol}</span>
                  )}
                </div>
                <a
                  href={`${SOLSCAN_URL}/token/${token.mint.toBase58()}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[#666] hover:text-[#f9cd4f] p-1"
                  onClick={(e) => e.stopPropagation()}
                >
                  <ExternalLink className="w-4 h-4" />
                </a>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
